import { getTestProps } from 'lib/testing/utils';
import styles from './Breadcrumb.module.scss';
import { SvgIcon } from 'components/helpers/SvgIcon';
import NextLink from 'next/link';
import classNames from 'classnames';
import { buildBreadcrumbJsonLd, BreadcrumbPair } from 'lib/seo/seoComponentsSchema';
import { useGlobalData } from 'lib/hooks/sitecore/useGlobalData';

export interface BreadcrumbInfo {
  url: string;
  title: React.ReactNode;
}
export interface BreadcrumbPlainProps {
  ancestors: BreadcrumbInfo[];
  isBreadcrumbForHero?: boolean;
  suppressJsonLd?: boolean;
}

export const Breadcrumb = ({
  ancestors,
  isBreadcrumbForHero,
  suppressJsonLd,
}: BreadcrumbPlainProps): JSX.Element => {
  const { siteName } = useGlobalData();
  const extractTitle = (title: React.ReactNode): string => {
    if (typeof title === 'string') return title;
    const t = (title ?? null) as {
      props?: { field?: { value?: string }; text?: string | unknown };
    } | null;
    const raw = t?.props?.field?.value ?? t?.props?.text ?? '';
    return (raw ?? '').toString();
  };

  // Build BreadcrumbList JSON-LD from ancestors (skip items without a string title)
  const breadcrumbPairs: BreadcrumbPair[] = (ancestors || [])
    .map(({ url, title }) => ({ url, name: extractTitle(title).trim() }))
    .filter(({ name }) => name.length > 0);

  const breadcrumbJsonLd = buildBreadcrumbJsonLd(breadcrumbPairs);
  return (
    <nav
      aria-label="Breadcrumb"
      className={classNames(styles.breadcrumb, {
        [styles.breadcrumbPaddingForHero]: isBreadcrumbForHero,
        // CMSMGR-24514: Hide breadcrumb on print for GMFS (standalone component, not inside hero wrapper).
        [`printHide`]: isBreadcrumbForHero || siteName === 'GMFS',
      })}
      data-component="authorable/shared/site-structure/breadcrumb"
      {...getTestProps(`breadcrumb`)}
    >
      {!suppressJsonLd && breadcrumbJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
        />
      )}
      <ul className={styles.breadcrumbList}>
        {ancestors.map((ancestor, index) => {
          const { url, title } = ancestor || {};
          const isActive = index === ancestors.length - 1;

          return (
            <li className={styles.breadcrumbListItem} key={url} {...getTestProps(`parent-page`)}>
              <NextLink
                className={classNames(styles.breadcrumbLinkWrapper, {
                  [styles.breadcrumbActive]: isActive,
                })}
                href={url}
              >
                {title}
              </NextLink>
              {!isActive && (
                <SvgIcon
                  className={styles.breadcrumbIcon}
                  fill="none"
                  icon="slash"
                  size="xs"
                  viewBox="0 0 16 16"
                />
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};
